import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Box, Divider, Grid } from '@material-ui/core';
import useIsMobile from '../../../../common/react/useIsMobile';
import useScrollToSection from '../../../hooks/navigation/useScrollToSection';
import stickyFooterContainerStyles from './StickyFooterContainerStyles';
import customise from '../../../constants/customise';
import { CustomiseSections } from '../../../../common/enum/CustomiseSections';
import { IEmissions } from '../../../../../../common/model/IEmissions';
import { ScrollInViewBehaviourType } from '../../../../common/service/scrollInView';
import ActionButtonsGridElement from './actionButtonsGridElement/ActionButtonsGridElement';
import ButtonViewDetails from '../../../../common/components/sticky/ButtonViewDetails';
import DotsLoader from '../../../../common/components/dotsLoader/DotsLoader';
import EmissionsWithLabel from './emissionsWithLabel/EmissionsWithLabel';
import usePlatformConfig from '../../../../common/react/usePlatformConfig';
import useToggleFinCalcSpc from '../../../../common/react/useToggleFinCalcSpc';
import FinanceProductGridElement from './financeProductGridElement/FinanceProductGridElement';
import FinanceCalculatorSpcContainer from '../../../../gqlCommon/components/financeCalculator/FinanceCalculatorSpcContainer';
import { useFinanceCarConfiguration } from '../../../../gqlCommon/hooks/useFinanceCarConfiguration';
import usePriceValidation from '../../../../gqlCommon/hooks/currency/usePriceValidation';
import { FinanceResultType } from '../../../../__generated__/globalTypes';
import useTheme from '@material-ui/core/styles/useTheme';
import { ISpcTheme } from '../../../../common/theme/ISpcTheme';
import { IFinanceCarConfiguration } from '../../../../gqlCommon/model/financeWidgetConfig/IFinanceCarConfiguration';
import useSalesEnablementContext from '../../../../gqlCommon/hooks/useSalesEnablementContext';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { hasFinanceProviderErrorVar } from '../../../../gqlCommon/graphql/cache';
import useSectionScrollWatcher from '../../../../common/hooks/useSectionScrollWatcher';
import useCustomizeCarContext from '../../../hooks/useCustomizeCarContext';

const StickyFooterContainer: FunctionComponent = () => {
  const style = stickyFooterContainerStyles();
  const isMobile = useIsMobile();
  const { scrollToSection } = useScrollToSection();
  const { isFinCalcSpcModalOpen, toggleFinanceCalculatorSpc } = useToggleFinCalcSpc();
  const [financeCarConfiguration, setFinanceCarConfiguration] = useState<IFinanceCarConfiguration>();
  const summarySection = document.getElementById(`${customise.DOM_ID_PREFIX}${CustomiseSections.SUMMARY}`);
  const shouldDisplaySummaryLabel = useMemo(() => !!summarySection && isMobile, [isMobile, summarySection]);
  const { selectedCar, loading } = useCustomizeCarContext();
  const carData = selectedCar?.car;
  const {
    platformConfig: { isComplexModel },
  } = usePlatformConfig();
  const {
    spc: {
      customise: { displayEmissions },
    },
  } = useTheme<ISpcTheme>();
  const { isMultiCurrency } = usePriceValidation(selectedCar?.prices.carPrice.originalPrice);
  const intl = useIntl();

  // @TODO: Will be completed on XPD-1742
  const emissionsData: IEmissions = {
    emissions: '127g/km',
    label: 'WLTP CO2 Emissions',
  };

  const onViewDetailsClick = useCallback(() => {
    scrollToSection(CustomiseSections.SUMMARY, ScrollInViewBehaviourType.SMOOTH);
  }, []);

  const { isSectionInView, checkSectionVisibility } = useSectionScrollWatcher(
    `${customise.DOM_ID_PREFIX}${CustomiseSections.SUMMARY}`,
  );

  const { isInSalesEnablement } = useSalesEnablementContext();
  const { getFinanceCarConfiguration } = useFinanceCarConfiguration();

  useEffect(() => {
    if (carData) {
      setFinanceCarConfiguration(
        getFinanceCarConfiguration(
          carData.code,
          selectedCar?.prices.carPrice.originalPrice,
          carData.trim.attributes.label,
          carData.variant?.attributes.name,
          carData?.attributes?.priceLabel,
        ),
      );
    }
  }, [carData]);

  useEffect(() => {
    if (selectedCar?.finance.type === FinanceResultType.error && !hasFinanceProviderErrorVar()) {
      hasFinanceProviderErrorVar(true);
      toast.error(
        intl.formatMessage({ id: 'trans__configurator__errors__customise_page_message_finance_failing' }),
        {},
      );
    }
  }, [selectedCar]);

  useEffect(() => {
    if (!loading) {
      document.addEventListener('scroll', checkSectionVisibility);

      return () => {
        document.removeEventListener('scroll', checkSectionVisibility);
      };
    }
  }, [loading, checkSectionVisibility]);

  if (loading && !selectedCar) {
    return (
      <Box className={style.footerWrapperLoader} data-test="container:sticky_footer_loader">
        <DotsLoader />
      </Box>
    );
  }

  return (
    <>
      <Grid
        alignItems="center"
        className={clsx(style.footerWrapper, { [style.hideOnMobile]: isSectionInView })}
        data-test="container:sticky_footer"
        container
      >
        <Grid item lg={8} md={10} sm={12} className={style.footerContent}>
          {
            <FinanceProductGridElement
              setIsFinCalcSpcModalOpen={() => toggleFinanceCalculatorSpc(true)}
              isFinCalcSpcModalOpen={isFinCalcSpcModalOpen}
              isCash={selectedCar?.finance?.type === FinanceResultType.cash}
              isMultiCurrency={isMultiCurrency}
              isCtaDisabled={loading}
            />
          }
          {isComplexModel && displayEmissions && (
            <>
              <Divider orientation="vertical" className={style.divider} />
              <EmissionsWithLabel emissions={emissionsData.emissions} label={emissionsData.label} />
            </>
          )}
        </Grid>
        {!isInSalesEnablement && <ActionButtonsGridElement disabled={loading} />}
        {shouldDisplaySummaryLabel && (
          <ButtonViewDetails
            onClick={onViewDetailsClick}
            position="top"
            captionId="trans__configurator__customise_page_sticky_footer__summary_link"
          />
        )}
      </Grid>
      {financeCarConfiguration && <FinanceCalculatorSpcContainer financeCarConfiguration={financeCarConfiguration} />}
    </>
  );
};

export default StickyFooterContainer;
